import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Known addresses → real coordinates
const PONENT_35: [number, number] = [41.545347, 1.951222];
const KNOWN_COORDS: Array<{ match: RegExp; coords: [number, number] }> = [
  { match: /ponent[,\s]*35/i, coords: PONENT_35 },
  { match: /viladecavalls/i, coords: [41.5636, 1.9558] },
  { match: /terrassa/i, coords: [41.5636, 2.0106] },
  { match: /sabadell/i, coords: [41.5432, 2.1094] },
  { match: /sant\s*cugat/i, coords: [41.4726, 2.0864] },
  { match: /rub[ií]/i, coords: [41.4938, 2.0335] },
  { match: /vall[eè]s\s*occidental/i, coords: [41.545347, 1.951222] },
];

const BASE_COORDS: [number, number] = [41.545347, 1.951222];

function getCoords(address: string): [number, number] {
  for (const k of KNOWN_COORDS) {
    if (k.match.test(address)) return k.coords;
  }
  // Fallback: deterministic offset
  let hash = 0;
  for (let i = 0; i < address.length; i++) {
    hash = (hash << 5) - hash + address.charCodeAt(i);
  }
  return [BASE_COORDS[0] + (hash % 100) * 0.0001, BASE_COORDS[1] + ((hash >> 8) % 100) * 0.0001];
}

interface Props {
  locations: { address: string; title: string; price?: string }[];
  single?: boolean;
  className?: string;
}

export default function PropertyMap({ locations, single = false, className = "" }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  const positions = locations.map((loc) => getCoords(loc.address));

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: positions[0] || BASE_COORDS,
      zoom: single ? 16 : 13,
      scrollWheelZoom: true,
    });

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: '&copy; Esri, Maxar, Earthstar Geographics' }
    ).addTo(map);

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { attribution: '' }
    ).addTo(map);

    positions.forEach((pos, i) => {
      const marker = L.marker(pos, { icon: defaultIcon }).addTo(map);
      const popupContent = `<strong>${locations[i].title}</strong>${locations[i].price ? `<br/>${locations[i].price}` : ""}`;
      marker.bindPopup(popupContent);
    });

    if (!single && positions.length > 1) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      className={`rounded-2xl overflow-hidden ${className}`}
      style={{ height: single ? 300 : 400 }}
    />
  );
}
